import { DocumentSectionSpec, SectionOptionSchema } from '@/entities/templates';

import { AI_OPTION_SLOT_NAME } from '../config';

import { isAiSlotName } from './aiSlots';

const SINGLE_SLOT_TOKEN_RE = /^\{\{\s*([\p{L}\p{N}_]+)\s*\}\}$/u;

export const isAiOption = (option: SectionOptionSchema): boolean => {
  const match = option.text.trim().match(SINGLE_SLOT_TOKEN_RE);

  if (!match || !isAiSlotName(match[1])) {
    return false;
  }

  return Object.keys(option.slots ?? {}).every(isAiSlotName);
};

export const createAiOption = (): SectionOptionSchema => ({
  text: `{{${AI_OPTION_SLOT_NAME}}}`,
  slots: { [AI_OPTION_SLOT_NAME]: [] },
});

export const getAiOptionIndex = (section: DocumentSectionSpec): number =>
  (section.options ?? []).findIndex(isAiOption);

export const getLastStrictOptionIndex = (
  section?: DocumentSectionSpec,
): number =>
  (section?.options ?? []).reduce(
    (lastIndex, option, optionIndex) =>
      isAiOption(option) ? lastIndex : optionIndex,
    -1,
  );

export const findLastSectionWithStrictOptions = (
  sections: DocumentSectionSpec[],
): number =>
  sections.reduce(
    (lastIndex, section, sectionIndex) =>
      getLastStrictOptionIndex(section) >= 0 ? sectionIndex : lastIndex,
    -1,
  );

type GetStrictInsertIndexArgs = {
  options: SectionOptionSchema[];
  insertAt: number;
};

// The AI option always renders after the strict options, so strict options
// are inserted before it.
export const getStrictInsertIndex = ({
  options,
  insertAt,
}: GetStrictInsertIndexArgs): number => {
  const aiOptionIndex = options.findIndex(isAiOption);

  return aiOptionIndex >= 0 ? Math.min(insertAt, aiOptionIndex) : insertAt;
};
